import { useState } from "react";
import {
	AlertCircle,
	ArrowLeft,
	Calendar,
	CheckCircle,
	ChevronRight,
	Clock,
	Filter,
	RefreshCw,
	RotateCcw,
	Search,
	User,
} from "lucide-react";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Money } from "@workspace/accounting/components";
import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";
import {
	CardDescription,
	CardHeader,
	CardTitle,
} from "@workspace/ui/components/card";
import { Input } from "@workspace/ui/components/input";
import { Label } from "@workspace/ui/components/label";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@workspace/ui/components/popover";
import { ScrollArea } from "@workspace/ui/components/scroll-area";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@workspace/ui/components/select";
import { Spinner } from "@workspace/ui/components/spinner";

import { eposApi } from "../../api";
import type { Order, RefundStatus } from "../../types";
import { useEposContext } from "../epos-context";
import { RefundsNeedingAttention } from "./refunds-needing-attention";

const REFUND_REASONS = [
	{ value: "customer-dissatisfied", label: "Customer Dissatisfied" },
	{ value: "wrong-order", label: "Wrong Order" },
	{ value: "quality-issue", label: "Quality Issue" },
	{ value: "overcharged", label: "Overcharged" },
	{ value: "duplicate-charge", label: "Duplicate Charge" },
	{ value: "cancelled-order", label: "Cancelled Order" },
	{ value: "other", label: "Other" },
] as const;

const REFUND_METHODS = [
	{ value: "cash", label: "Cash" },
	{ value: "card", label: "Card" },
	{ value: "store-credit", label: "Store Credit" },
] as const;

interface RefundHistoryViewProps {
	onClose: () => void;
	onSelectOrder: (order: Order) => void;
}

const getStatusBadge = (status: RefundStatus) => {
	switch (status) {
		case "Succeeded":
			return (
				<Badge variant="default" className="gap-1">
					<CheckCircle className="size-3" />
					Completed
				</Badge>
			);
		case "Failed":
			return (
				<Badge variant="destructive" className="gap-1">
					<AlertCircle className="size-3" />
					Failed
				</Badge>
			);
		case "Processing":
			return (
				<Badge variant="secondary" className="gap-1">
					<Clock className="size-3" />
					Processing
				</Badge>
			);
		default:
			return (
				<Badge variant="outline" className="gap-1">
					<Clock className="size-3" />
					Pending
				</Badge>
			);
	}
};

export function RefundHistoryView({
	onClose,
	onSelectOrder,
}: RefundHistoryViewProps) {
	const { tenant, venueId, data, timeZone, operations } = useEposContext();
	const queryClient = useQueryClient();

	// Filter state
	const [searchQuery, setSearchQuery] = useState("");
	const [methodFilter, setMethodFilter] = useState<string>("all");
	const [reasonFilter, setReasonFilter] = useState<string>("all");
	const [isFilterOpen, setIsFilterOpen] = useState(false);
	const [retryingRefundId, setRetryingRefundId] = useState<string | null>(null);

	// Retry mutation
	const retryMutation = useMutation({
		mutationFn: (args: { orderId: string; refundId: string }) =>
			eposApi.retryRefund(tenant, venueId, args.orderId, args.refundId),
		onSuccess(result) {
			if (result.success) {
				toast.success("Refund retry successful");
				// HA-954: the till's EPOS read is composed on the server; refetch it rather than set it from this result.
				void queryClient.invalidateQueries({
					queryKey: [tenant, venueId, "epos"],
				});
			} else {
				toast.error("Refund retry failed", { description: result.message });
			}
			setRetryingRefundId(null);
		},
		onError() {
			toast.error("Failed to retry refund");
			setRetryingRefundId(null);
		},
	});

	// Get all orders with refunds from both active and recent orders
	const allOrders = data.orders;
	const ordersWithRefunds = allOrders.filter(
		(order) => order.refunds && order.refunds.length > 0,
	);

	// Flatten refunds with order info for easier display
	const allRefunds = ordersWithRefunds.flatMap((order) =>
		(order.refunds ?? []).map((refund) => ({
			...refund,
			order,
		})),
	);

	// Sort by timestamp descending (most recent first)
	const sortedRefunds = allRefunds.sort(
		(a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
	);

	// Apply filters
	const filteredRefunds = sortedRefunds.filter((refund) => {
		// Search filter (order ID or staff name)
		if (searchQuery) {
			const query = searchQuery.toLowerCase();
			const matchesOrderId = refund.order.id.toLowerCase().includes(query);
			const matchesStaff = refund.staff.label.toLowerCase().includes(query);
			if (!matchesOrderId && !matchesStaff) return false;
		}

		// Method filter
		if (methodFilter !== "all" && refund.method !== methodFilter) {
			return false;
		}

		// Reason filter
		if (reasonFilter !== "all" && refund.reason !== reasonFilter) {
			return false;
		}

		return true;
	});

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString("en-US", {
			timeZone,
			month: "short",
			day: "numeric",
			year: "numeric",
		});
	};

	const formatTime = (dateString: string) => {
		return new Date(dateString).toLocaleTimeString("en-US", {
			timeZone,
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const getReasonLabel = (value: string) => {
		return REFUND_REASONS.find((r) => r.value === value)?.label ?? value;
	};

	const getMethodLabel = (value: string) => {
		return REFUND_METHODS.find((m) => m.value === value)?.label ?? value;
	};

	const activeFilterCount =
		(methodFilter !== "all" ? 1 : 0) + (reasonFilter !== "all" ? 1 : 0);

	return (
		<div className="flex h-full flex-col">
			<CardHeader className="pb-3">
				<div className="flex items-center justify-between">
					<div>
						<CardTitle className="flex items-center gap-2 text-lg">
							<RotateCcw className="size-5" />
							Refund History
						</CardTitle>
						<CardDescription>
							{filteredRefunds.length} refund
							{filteredRefunds.length !== 1 ? "s" : ""} found
						</CardDescription>
					</div>
					<Button variant="outline" size="sm" onClick={onClose}>
						<ArrowLeft className="mr-2 size-4" />
						Back
					</Button>
				</div>
			</CardHeader>

			<div className="px-4 pb-3 empty:hidden">
				<RefundsNeedingAttention
					tenant={tenant}
					venueId={venueId}
					operations={operations}
				/>
			</div>

			{/* Search and Filters */}
			<div className="border-t px-4 py-3">
				<div className="flex gap-2">
					<div className="relative flex-1">
						<Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
						<Input
							placeholder="Search by order ID or staff..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							className="pl-9"
						/>
					</div>
					<Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
						<PopoverTrigger
							render={
								<Button variant="outline" size="icon" className="relative">
									<Filter className="size-4" />
									{activeFilterCount > 0 && (
										<span className="absolute -right-1 -top-1 flex size-5 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">
											{activeFilterCount}
										</span>
									)}
								</Button>
							}
						/>
						<PopoverContent className="w-72" align="end">
							<div className="space-y-4">
								<h4 className="font-medium">Filters</h4>

								<div className="space-y-2">
									<Label>Method</Label>
									<Select value={methodFilter} onValueChange={setMethodFilter}>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="all">All Methods</SelectItem>
											{REFUND_METHODS.map((method) => (
												<SelectItem key={method.value} value={method.value}>
													{method.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								<div className="space-y-2">
									<Label>Reason</Label>
									<Select value={reasonFilter} onValueChange={setReasonFilter}>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="all">All Reasons</SelectItem>
											{REFUND_REASONS.map((reason) => (
												<SelectItem key={reason.value} value={reason.value}>
													{reason.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								{activeFilterCount > 0 && (
									<Button
										variant="ghost"
										size="sm"
										className="w-full"
										onClick={() => {
											setMethodFilter("all");
											setReasonFilter("all");
										}}
									>
										Clear Filters
									</Button>
								)}
							</div>
						</PopoverContent>
					</Popover>
				</div>
			</div>

			<ScrollArea className="flex-1 border-t">
				{filteredRefunds.length === 0 ? (
					<div className="flex flex-col items-center justify-center p-8 text-center text-muted-foreground">
						<RotateCcw className="mb-4 size-12 opacity-50" />
						<p className="text-lg font-medium">No refunds found</p>
						<p className="text-sm">
							{searchQuery || activeFilterCount > 0
								? "Try adjusting your filters"
								: "Refunds will appear here after processing"}
						</p>
					</div>
				) : (
					<div className="divide-y">
						{filteredRefunds.map((refund) => (
							<div
								key={`${refund.order.id}-${refund.id}`}
								className="flex w-full items-center gap-3 p-4 transition-colors hover:bg-muted/50"
							>
								<button
									type="button"
									className="flex flex-1 items-center gap-3 text-left"
									onClick={() => onSelectOrder(refund.order)}
								>
									<div className="flex-1 space-y-1">
										<div className="flex flex-wrap items-center gap-2">
											<span className="font-medium">
												Order #{refund.order.id}
											</span>
											<Badge
												variant={
													refund.order.status === "Refunded"
														? "destructive"
														: "secondary"
												}
												className="text-xs"
											>
												{refund.order.status === "Refunded"
													? "Full Refund"
													: "Partial"}
											</Badge>
											{getStatusBadge(refund.status)}
										</div>
										<div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
											<span className="flex items-center gap-1">
												<Calendar className="size-3" />
												{formatDate(refund.timestamp)}{" "}
												{formatTime(refund.timestamp)}
											</span>
											<span className="flex items-center gap-1">
												<User className="size-3" />
												{refund.staff.label}
											</span>
										</div>
										<div className="flex flex-wrap gap-2 pt-1">
											<Badge variant="outline" className="text-xs">
												{getMethodLabel(refund.method)}
											</Badge>
											<Badge variant="outline" className="text-xs">
												{getReasonLabel(refund.reason)}
											</Badge>
										</div>
										{refund.status === "Failed" && refund.errorMessage && (
											<p className="text-xs text-destructive">
												{refund.errorMessage}
											</p>
										)}
									</div>
									<div className="text-right">
										<div className="font-medium text-destructive">
											-<Money {...refund.total} />
										</div>
										<ChevronRight className="ml-auto mt-1 size-4 text-muted-foreground" />
									</div>
								</button>
								{refund.status === "Failed" && refund.method === "card" && (
									<Button
										variant="outline"
										size="sm"
										onClick={(e) => {
											e.stopPropagation();
											setRetryingRefundId(refund.id);
											retryMutation.mutate({
												orderId: refund.order.id,
												refundId: refund.id,
											});
										}}
										disabled={retryingRefundId === refund.id}
									>
										{retryingRefundId === refund.id ? (
											<Spinner />
										) : (
											<RefreshCw className="mr-1 size-3" />
										)}
										Retry
									</Button>
								)}
							</div>
						))}
					</div>
				)}
			</ScrollArea>
		</div>
	);
}
